import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Typography,
  Skeleton,
  Button,
  Chip,
  InputBase,
  IconButton,
} from "@mui/material";
import {
  FaArrowRight,
  FaSchool,
  FaClipboardList,
  FaPlay,
  FaUserGraduate,
  FaSearch,
  FaTimes,
} from "react-icons/fa";
import { HiSparkles } from "react-icons/hi2";
import { API_URL } from "../config";
import Ads1Component from "../ads/Ads1";
import { updateStudentCoins } from "../utils/coin";
import { motion, AnimatePresence } from "framer-motion";

/* ─────────────────────────────────────────────
   Design tokens
───────────────────────────────────────────── */
const T = {
  sky: "#0EA5E9",
  skyDeep: "#0284C7",
  skyLight: "#BAE6FD",
  skySoft: "rgba(14,165,233,0.08)",
  ink: "#0C1A2E",
  inkMid: "#374151",
  inkLight: "#6B7280",
  inkFaint: "#94A3B8",
  violet: "#7C3AED",
  violetLight: "rgba(124,58,237,0.10)",
  surface: "#FFFFFF",
  surfaceAlt: "#F8FAFC",
  border: "rgba(14,165,233,0.12)",
  borderSolid: "#E2E8F0",
};

/* Motion-enabled MUI primitives — lets Framer Motion drive real
   hover/tap states instead of relying purely on CSS transitions. */
const MotionBox = motion(Box);
const MotionButton = motion(Button);

/* Spring used across hover/tap micro-interactions */
const hoverSpring = { type: "spring", stiffness: 300, damping: 20 };

/* ─────────────────────────────────────────────
   Framer variants
───────────────────────────────────────────── */
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  },
};

const cardVariant = {
  hidden: { opacity: 0, y: 28, scale: 0.97 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  },
};

const containerStagger = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.08, delayChildren: 0.05 },
  },
};

/* ─────────────────────────────────────────────
   Sub-components
───────────────────────────────────────────── */

/** Pill badge with animated dot */
const PulseBadge = ({ label }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.9,
      px: 1.8,
      py: 0.65,
      borderRadius: 99,
      background: T.skySoft,
      border: "1px solid rgba(14,165,233,0.22)",
      backdropFilter: "blur(8px)",
    }}
  >
    <Box
      sx={{
        width: 6,
        height: 6,
        borderRadius: "50%",
        background: T.sky,
        boxShadow: `0 0 7px ${T.sky}`,
        animation: "pulse 2s ease-in-out infinite",
        "@keyframes pulse": {
          "0%,100%": { opacity: 1, transform: "scale(1)" },
          "50%": { opacity: 0.4, transform: "scale(0.75)" },
        },
      }}
    />
    <Typography
      sx={{
        fontSize: "0.65rem",
        fontWeight: 800,
        color: T.skyDeep,
        textTransform: "uppercase",
        letterSpacing: "1.2px",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      {label}
    </Typography>
  </Box>
);

/** Animated search bar — filters the exam grid client-side */
const SearchBar = ({ value, onChange, onClear }) => {
  const [focused, setFocused] = useState(false);
  return (
    <MotionBox
      animate={{
        boxShadow: focused
          ? `0 0 0 3px ${T.skySoft}, 0 10px 28px rgba(14,165,233,0.12)`
          : "0 2px 10px rgba(12,26,46,0.04)",
        borderColor: focused ? T.sky : T.borderSolid,
      }}
      transition={{ duration: 0.25 }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        maxWidth: 440,
        mx: "auto",
        px: 2,
        py: 1.1,
        borderRadius: "14px",
        background: T.surface,
        border: `1px solid ${T.borderSolid}`,
      }}
    >
      <FaSearch size={14} color={T.inkFaint} />
      <InputBase
        value={value}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder="Search for a subject or exam…"
        sx={{
          flex: 1,
          fontSize: "0.88rem",
          fontFamily: "'DM Sans', sans-serif",
          color: T.ink,
        }}
      />
      <AnimatePresence>
        {value && (
          <motion.div
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.6 }}
          >
            <IconButton size="small" onClick={onClear} sx={{ p: 0.5 }}>
              <FaTimes size={12} color={T.inkFaint} />
            </IconButton>
          </motion.div>
        )}
      </AnimatePresence>
    </MotionBox>
  );
};

/** Feature card (Subject Lessons / Student Portal) */
const FeatureCard = ({
  icon: Icon,
  title,
  description,
  cta,
  onClick,
  outlined = false,
}) => (
  <MotionBox
    variants={cardVariant}
    whileHover={{ y: -6, transition: hoverSpring }}
    whileTap={{ scale: 0.985 }}
    onClick={onClick}
    sx={{
      height: "100%",
      p: { xs: 3, md: 3.5 },
      borderRadius: "20px",
      background: T.surface,
      border: `1px solid ${T.borderSolid}`,
      boxShadow: "0 2px 16px rgba(12,26,46,0.05)",
      display: "flex",
      flexDirection: "column",
      gap: 1.5,
      cursor: "pointer",
      position: "relative",
      overflow: "hidden",
      transition: "box-shadow 0.3s ease, border-color 0.3s ease",
      "&:hover": {
        boxShadow: outlined
          ? "0 14px 36px rgba(124,58,237,0.16)"
          : "0 14px 36px rgba(14,165,233,0.16)",
        borderColor: outlined ? "rgba(124,58,237,0.3)" : "rgba(14,165,233,0.3)",
      },
    }}
  >
    {/* decorative glow */}
    <Box
      sx={{
        position: "absolute",
        top: -40,
        right: -40,
        width: 120,
        height: 120,
        borderRadius: "50%",
        background: outlined ? T.violetLight : T.skySoft,
        opacity: 0.6,
      }}
    />

    <motion.div
      whileHover={{ rotate: -10, scale: 1.08 }}
      transition={hoverSpring}
      style={{ width: 44, height: 44, position: "relative", zIndex: 1 }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: "12px",
          background: outlined ? T.violetLight : T.skySoft,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon size={20} color={outlined ? T.violet : T.skyDeep} />
      </Box>
    </motion.div>

    <Typography
      sx={{
        fontFamily: "'Syne', sans-serif",
        fontWeight: 700,
        fontSize: "1.05rem",
        color: T.ink,
        lineHeight: 1.25,
        position: "relative",
        zIndex: 1,
      }}
    >
      {title}
    </Typography>

    <Typography
      sx={{
        color: T.inkLight,
        fontSize: "0.88rem",
        lineHeight: 1.65,
        flex: 1,
        position: "relative",
        zIndex: 1,
      }}
    >
      {description}
    </Typography>

    <MotionButton
      variant={outlined ? "outlined" : "contained"}
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.96 }}
      transition={hoverSpring}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      endIcon={<FaArrowRight size={11} />}
      sx={{
        alignSelf: "flex-start",
        mt: 0.5,
        borderRadius: "10px",
        px: 2.5,
        py: 0.9,
        position: "relative",
        zIndex: 1,
        fontFamily: "'DM Sans', sans-serif",
        fontWeight: 700,
        fontSize: "0.82rem",
        textTransform: "none",
        letterSpacing: "0.2px",
        ...(outlined
          ? {
              borderColor: T.violet,
              color: T.violet,
              "&:hover": { borderColor: "#6D28D9", background: T.violetLight },
            }
          : {
              background: `linear-gradient(135deg, ${T.sky}, ${T.skyDeep})`,
              boxShadow: "0 4px 14px rgba(14,165,233,0.32)",
              color: "#fff",
              border: "none",
              "&:hover": {
                background: `linear-gradient(135deg, ${T.skyDeep}, #0369A1)`,
                boxShadow: "0 6px 18px rgba(14,165,233,0.4)",
              },
            }),
      }}
    >
      {cta}
    </MotionButton>
  </MotionBox>
);

/** Single exam card */
const ExamCard = ({ exam, onClick }) => (
  <MotionBox
    variants={cardVariant}
    whileHover={{ y: -8, transition: hoverSpring }}
    whileTap={{ scale: 0.97 }}
    onClick={onClick}
    sx={{
      borderRadius: "20px",
      overflow: "hidden",
      border: `1px solid ${T.borderSolid}`,
      background: T.surface,
      cursor: "pointer",
      transition: "box-shadow 0.3s ease, border-color 0.3s ease",
      "&:hover": {
        borderColor: "rgba(14,165,233,0.4)",
        boxShadow: "0 16px 40px rgba(14,165,233,0.15)",
        "& .exam-img": { transform: "scale(1.09)" },
        "& .badge": {
          background: `linear-gradient(135deg, ${T.sky}, ${T.skyDeep})`,
          color: "#fff",
          borderColor: "transparent",
        },
        "& .badge-text": { color: "#fff" },
        "& .badge-arrow": { transform: "translateX(3px)" },
      },
    }}
  >
    <Box sx={{ height: 155, overflow: "hidden", position: "relative" }}>
      <Box
        className="exam-img"
        component="img"
        src={
          exam.exam_img ||
          "https://res.cloudinary.com/dk5mfu099/image/upload/v1765533020/premium_photo-1725296624057-d723d163943e_gbkonj.jpg"
        }
        alt={exam.name}
        sx={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          transition: "transform 0.45s cubic-bezier(0.22,1,0.36,1)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(to top, rgba(12,26,46,0.55) 0%, rgba(12,26,46,0.05) 55%, transparent 100%)",
        }}
      />
      <Chip
        label="Practice"
        size="small"
        sx={{
          position: "absolute",
          bottom: 10,
          left: 12,
          height: 22,
          fontSize: "0.6rem",
          fontWeight: 800,
          fontFamily: "'DM Sans', sans-serif",
          letterSpacing: "0.8px",
          textTransform: "uppercase",
          background: "rgba(255,255,255,0.18)",
          backdropFilter: "blur(8px)",
          color: "#fff",
          border: "1px solid rgba(255,255,255,0.25)",
        }}
      />
    </Box>

    <Box sx={{ px: 2, pt: 2, pb: 2.2, textAlign: "center" }}>
      <Typography
        sx={{
          fontFamily: "'Syne', sans-serif",
          fontWeight: 700,
          color: T.ink,
          fontSize: "0.88rem",
          mb: 1.5,
          lineHeight: 1.35,
        }}
      >
        {exam.name}
      </Typography>
      <Box
        className="badge"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.65,
          px: 1.6,
          py: 0.55,
          borderRadius: 99,
          background: T.skySoft,
          border: "1px solid rgba(14,165,233,0.22)",
          color: T.skyDeep,
          transition: "all 0.28s ease",
        }}
      >
        <Typography
          className="badge-text"
          sx={{
            fontSize: "0.65rem",
            fontWeight: 800,
            fontFamily: "'DM Sans', sans-serif",
            color: "inherit",
            letterSpacing: "0.5px",
          }}
        >
          Start Practice
        </Typography>
        <Box
          className="badge-arrow"
          sx={{ display: "flex", transition: "transform 0.25s ease" }}
        >
          <FaArrowRight size={8} />
        </Box>
      </Box>
    </Box>
  </MotionBox>
);

/** Institution card */
const InstitutionCard = ({ onClick }) => (
  <MotionBox
    variants={cardVariant}
    whileHover={{ y: -8, transition: hoverSpring }}
    whileTap={{ scale: 0.97 }}
    onClick={onClick}
    sx={{
      borderRadius: "20px",
      overflow: "hidden",
      border: "1px solid rgba(124,58,237,0.18)",
      background: T.surface,
      cursor: "pointer",
      transition: "box-shadow 0.3s ease, border-color 0.3s ease",
      "&:hover": {
        borderColor: "rgba(124,58,237,0.42)",
        boxShadow: "0 16px 40px rgba(124,58,237,0.16)",
        "& .inst-dot-bg": { transform: "scale(1.1)", opacity: 0.22 },
        "& .inst-badge": {
          background: `linear-gradient(135deg, ${T.violet}, #9333EA)`,
          color: "#fff",
          borderColor: "transparent",
        },
        "& .inst-badge-text": { color: "#fff" },
      },
    }}
  >
    <Box
      sx={{
        height: 155,
        position: "relative",
        overflow: "hidden",
        background:
          "linear-gradient(145deg, #7C3AED 0%, #9333EA 60%, #A855F7 100%)",
      }}
    >
      <Box
        className="inst-dot-bg"
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0.13,
          backgroundImage:
            "radial-gradient(circle, #fff 1.2px, transparent 1.2px)",
          backgroundSize: "18px 18px",
          transition: "transform 0.45s ease, opacity 0.3s",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          width: 140,
          height: 140,
          borderRadius: "50%",
          border: "1px solid rgba(255,255,255,0.12)",
          top: -30,
          right: -30,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        <Box
          sx={{
            width: 50,
            height: 50,
            borderRadius: "14px",
            background: "rgba(255,255,255,0.18)",
            border: "1px solid rgba(255,255,255,0.28)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(8px)",
          }}
        >
          <FaSchool size={22} color="#fff" />
        </Box>
        <Typography
          sx={{
            fontSize: "0.62rem",
            fontWeight: 800,
            color: "rgba(255,255,255,0.82)",
            textTransform: "uppercase",
            letterSpacing: "1.8px",
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          Admission Test
        </Typography>
      </Box>
    </Box>

    <Box sx={{ px: 2, pt: 2, pb: 2.2, textAlign: "center" }}>
      <Typography
        sx={{
          fontFamily: "'Syne', sans-serif",
          fontWeight: 700,
          color: T.ink,
          fontSize: "0.88rem",
          mb: 1.5,
          lineHeight: 1.35,
        }}
      >
        Institution Exams
      </Typography>
      <Box
        className="inst-badge"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.65,
          px: 1.6,
          py: 0.55,
          borderRadius: 99,
          background: T.violetLight,
          border: "1px solid rgba(124,58,237,0.22)",
          color: T.violet,
          transition: "all 0.28s ease",
        }}
      >
        <Typography
          className="inst-badge-text"
          sx={{
            fontSize: "0.65rem",
            fontWeight: 800,
            fontFamily: "'DM Sans', sans-serif",
            color: "inherit",
            letterSpacing: "0.5px",
          }}
        >
          Explore
        </Typography>
        <FaArrowRight size={8} />
      </Box>
    </Box>
  </MotionBox>
);

/** Skeleton loader card */
const SkeletonCard = () => (
  <Box
    sx={{
      borderRadius: "20px",
      overflow: "hidden",
      border: `1px solid ${T.borderSolid}`,
      background: T.surface,
    }}
  >
    <Skeleton
      variant="rectangular"
      height={155}
      sx={{ bgcolor: "#F1F5F9", borderRadius: "20px 20px 0 0" }}
    />
    <Box sx={{ p: 2 }}>
      <Skeleton
        width="68%"
        height={20}
        sx={{ mb: 1.2, mx: "auto", bgcolor: "#F1F5F9", borderRadius: 2 }}
      />
      <Skeleton
        width="42%"
        height={26}
        sx={{ mx: "auto", bgcolor: "#F1F5F9", borderRadius: 99 }}
      />
    </Box>
  </Box>
);

/* ─────────────────────────────────────────────
   Main component
───────────────────────────────────────────── */
const HomePage = () => {
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    const refreshCoins = async () => {
      await updateStudentCoins();
    };
    refreshCoins();
  }, []);

  const isValidJson = (str) => {
    try {
      JSON.parse(str);
      return true;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchExams = async () => {
      try {
        const tokenString = localStorage.getItem("authToken");
        if (!tokenString || !isValidJson(tokenString)) return;
        const token = JSON.parse(tokenString);
        const res = await fetch(`${API_URL}/exams/`, {
          headers: { Authorization: `Bearer ${token.access}` },
        });
        if (res.ok) {
          const data = await res.json();
          setExams(data.exams.filter((e) => !e.is_postUTME));
        }
      } catch (err) {
        console.error("Failed to fetch exams:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchExams();
  }, []);

  const filteredExams = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return exams;
    return exams.filter((e) => e.name.toLowerCase().includes(q));
  }, [exams, query]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(160deg, #F0F9FF 0%, #FAFAFE 45%, #EFF6FF 100%)",
        pt: { xs: 3, md: 5 },
        pb: 12,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* ambient floating blobs — slow infinite drift via Framer Motion */}
      <MotionBox
        animate={{ y: [0, 26, 0], x: [0, 14, 0] }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
        sx={{
          position: "fixed",
          top: -160,
          right: -160,
          width: 520,
          height: 520,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(14,165,233,0.07) 0%, transparent 70%)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <MotionBox
        animate={{ y: [0, -22, 0], x: [0, -16, 0] }}
        transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
        sx={{
          position: "fixed",
          bottom: -200,
          left: -100,
          width: 480,
          height: 480,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(124,58,237,0.06) 0%, transparent 70%)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        {/* ── Ads ── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box
            sx={{
              mb: 5,
              borderRadius: "20px",
              overflow: "hidden",
              border: `1px solid ${T.border}`,
              boxShadow: "0 2px 14px rgba(14,165,233,0.07)",
            }}
          >
            <Ads1Component />
          </Box>
        </motion.div>

        {/* ── Feature Cards Row ── */}
        <motion.div variants={containerStagger} initial="hidden" animate="show">
          <Grid container spacing={2.5} sx={{ mb: 5 }}>
            <Grid item xs={12} sm={6}>
              <FeatureCard
                icon={FaPlay}
                title="Subject Lessons"
                description="Watch video lessons for each subject and expand your knowledge at your own pace."
                cta="Explore Subjects"
                onClick={() => navigate("/home/subjects")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FeatureCard
                icon={FaUserGraduate}
                title="Student Portal"
                description="Track your progress, view results, and manage your assignments all in one place."
                cta="Go to Portal"
                onClick={() => navigate("/student-portal")}
                outlined
              />
            </Grid>
          </Grid>
        </motion.div>

        {/* ── Section Header + Search ── */}
        <motion.div variants={fadeUp} initial="hidden" animate="show">
          <Box sx={{ textAlign: "center", mb: { xs: 4, md: 5 } }}>
            <PulseBadge
              label={
                loading
                  ? "Loading Exams"
                  : `${exams.length} Exam${
                      exams.length === 1 ? "" : "s"
                    } Available`
              }
            />

            <Typography
              variant="h3"
              sx={{
                mt: 2.5,
                mb: 1.5,
                fontFamily: "'Syne', sans-serif",
                fontWeight: 900,
                letterSpacing: "-1.8px",
                color: T.ink,
                fontSize: { xs: "2rem", sm: "2.4rem", md: "2.9rem" },
                lineHeight: 1.1,
              }}
            >
              Choose Your{" "}
              <Box
                component="span"
                sx={{
                  position: "relative",
                  display: "inline-block",
                  color: T.skyDeep,
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: 2,
                    left: 0,
                    width: "100%",
                    height: 4,
                    borderRadius: 99,
                    background: `linear-gradient(90deg, ${T.sky}, ${T.violet})`,
                    opacity: 0.35,
                  },
                }}
              >
                Exam
              </Box>
            </Typography>

            <Typography
              sx={{
                color: T.inkLight,
                fontSize: { xs: "0.9rem", md: "1rem" },
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 400,
                maxWidth: 420,
                mx: "auto",
                mb: 3,
              }}
            >
              Select an exam below and start practicing to ace your goals.
            </Typography>

            <SearchBar
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onClear={() => setQuery("")}
            />
          </Box>
        </motion.div>

        {/* ── Grid ── */}
        <AnimatePresence mode="wait">
          {loading ? (
            <Grid container spacing={2.5} key="loading">
              {[...Array(8)].map((_, i) => (
                <Grid item xs={6} sm={4} md={3} key={i}>
                  <SkeletonCard />
                </Grid>
              ))}
            </Grid>
          ) : filteredExams.length === 0 ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
            >
              <Box
                sx={{
                  textAlign: "center",
                  py: 14,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: "20px",
                    background: T.skySoft,
                    border: "1px solid rgba(14,165,233,0.18)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1,
                  }}
                >
                  <FaClipboardList
                    size={28}
                    color={T.skyDeep}
                    style={{ opacity: 0.55 }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontFamily: "'Syne', sans-serif",
                    fontWeight: 700,
                    color: T.ink,
                    fontSize: "1.1rem",
                  }}
                >
                  {query ? "No matching exams" : "No exams available"}
                </Typography>
                <Typography
                  sx={{
                    color: T.inkFaint,
                    fontSize: "0.88rem",
                    fontFamily: "'DM Sans', sans-serif",
                    maxWidth: 320,
                  }}
                >
                  {query
                    ? `We couldn't find any exam matching "${query}".`
                    : "Check back soon — new exams are added regularly."}
                </Typography>
                {query && (
                  <MotionButton
                    variant="outlined"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.96 }}
                    onClick={() => setQuery("")}
                    sx={{
                      mt: 1,
                      borderRadius: "10px",
                      textTransform: "none",
                      fontFamily: "'DM Sans', sans-serif",
                      fontWeight: 700,
                      borderColor: T.sky,
                      color: T.skyDeep,
                      "&:hover": {
                        borderColor: T.skyDeep,
                        background: T.skySoft,
                      },
                    }}
                  >
                    Clear search
                  </MotionButton>
                )}
              </Box>
            </motion.div>
          ) : (
            <motion.div
              key="grid"
              variants={containerStagger}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.1 }}
            >
              <Grid container spacing={2.5}>
                {filteredExams.map((exam) => (
                  <Grid item xs={6} sm={4} md={3} key={exam.id}>
                    <ExamCard
                      exam={exam}
                      onClick={() => navigate(`/subjects/${exam.id}`)}
                    />
                  </Grid>
                ))}

                {!query && (
                  <Grid item xs={6} sm={4} md={3}>
                    <InstitutionCard
                      onClick={() => navigate("/institutions")}
                    />
                  </Grid>
                )}
              </Grid>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Bottom sparkle note ── */}
        {!loading && filteredExams.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.6 }}
          >
            <Box
              sx={{
                mt: 7,
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <HiSparkles size={13} color={T.skyDeep} />
              <Typography
                sx={{
                  fontSize: "0.78rem",
                  color: T.inkFaint,
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                New exams and questions are added regularly.
              </Typography>
              <HiSparkles size={13} color={T.skyDeep} />
            </Box>
          </motion.div>
        )}
      </Container>
    </Box>
  );
};

export default HomePage;
